import { canAccessRoute, hasSpecialRole, ROUTE_PERMISSIONS } from './permissions';

const REDIRECT_PRIORITY_ROUTES = [
  '/medicore', // Dashboard principal
  '/medicore/inventory/list', // Inventario
  '/medicore/finance/accounts/list', // Contabilidad
  '/medicore/humanResources/employees/list', // RRHH
  '/medicore/patientInvoice/list', // Facturación
  '/medicore/rooms/list', // Habitaciones
  '/medicore/laboratories', // Servicios médicos
  '/medicore/nurse/shifts', // Enfermería
  '/medicore/insurance/providers/list', // Seguros
  '/medicore/userManagement/list', // Gestión de usuarios
];

/**
 * Encuentra la primera ruta a la que el usuario tiene acceso
 * @param {Object} user - Usuario actual
 * @returns {string|null} - Primera ruta accesible o null
 */
export const findFirstAccessibleRoute = (user) => {
  if (hasSpecialRole(user)) {
    return '/medicore';
  }

  if (!user || !user.permissions || user.permissions.length === 0) {
    return null;
  }

  const priorityRoute = REDIRECT_PRIORITY_ROUTES.find((route) => {
    if (canAccessRoute(user, route)) {
      return true;
    }
    return false;
  });

  if (priorityRoute) {
    return priorityRoute;
  }

  const fallbackRoute = Object.keys(ROUTE_PERMISSIONS).find((route) => {
    if (canAccessRoute(user, route)) {
      return true;
    }
    return false;
  });

  if (fallbackRoute) {
    return fallbackRoute;
  }

  return null;
};

/**
 * Obtiene la ruta de redirección apropiada para un usuario
 * @param {Object} user - Usuario actual
 * @returns {string} - Ruta de redirección
 */
export const getRedirectRoute = (user) => {
  if (hasSpecialRole(user)) {
    return '/medicore';
  }

  if (canAccessRoute(user, '/medicore')) {
    return '/medicore';
  }

  const firstAccessibleRoute = findFirstAccessibleRoute(user);

  return firstAccessibleRoute || '/auth/login';
};

/**
 * Verifica si el usuario necesita ser redirigido desde la ruta actual
 * @param {Object} user - Usuario actual
 * @param {string} currentRoute - Ruta actual
 * @returns {string|null} - Ruta de redirección o null si no necesita redirigir
 */
export const checkRedirectNeeded = (user, currentRoute) => {
  if (hasSpecialRole(user)) {
    return null;
  }

  if (canAccessRoute(user, currentRoute)) {
    return null;
  }

  return getRedirectRoute(user);
};

export const analyzeUserRoutes = (user) => {
  const analysis = {
    user: {
      username: user?.username,
      roles: user?.roles || [],
      permissionsCount: user?.permissions?.length || 0,
      isMaster: hasSpecialRole(user),
    },
    routes: {
      total: Object.keys(ROUTE_PERMISSIONS).length,
      accessible: [],
      denied: [],
      priority: {
        accessible: [],
        denied: [],
      },
    },
    redirect: {
      defaultRoute: getRedirectRoute(user),
      firstAccessible: findFirstAccessibleRoute(user),
    },
  };

  Object.keys(ROUTE_PERMISSIONS).forEach((route) => {
    if (canAccessRoute(user, route)) {
      analysis.routes.accessible.push(route);
    } else {
      analysis.routes.denied.push(route);
    }
  });

  REDIRECT_PRIORITY_ROUTES.forEach((route) => {
    if (canAccessRoute(user, route)) {
      analysis.routes.priority.accessible.push(route);
    } else {
      analysis.routes.priority.denied.push(route);
    }
  });

  return analysis;
};

export const logRouteAnalysis = (user) => {
  const analysis = analyzeUserRoutes(user);

  return analysis;
};
